import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../components/cos-icon.js';
import '../components/cos-button.js';
import { api } from '../services/api.js';
import { showToast } from '../utils/toast.js';

/**
 * <cos-needs-desktop> — Responsive Workstream B's capability-gate panel
 * (roam-responsive-pwa-spec.md §7). The one standard treatment every
 * `capability-registry.ts` gate renders through:
 *
 *   - variant="block"  — a full-panel gate for `blocked` routes. Renders
 *     INSTEAD OF the page (the router guard in app.ts never mounts the
 *     gated component at all).
 *   - variant="banner" — a slim strip above the page for `degraded` /
 *     `view-only` routes, which still render — the surface just carries
 *     a visible caveat.
 *
 * The headline is DEVICE-AWARE — it names the smallest device class
 * that works ("needs a tablet or larger screen" vs "needs a desktop"),
 * computed by the caller via `deviceHandoffLabel()` in
 * capability-registry.ts and passed in as `device-label`, so this
 * component stays a dumb renderer of whatever the registry decided.
 *
 * "Send me this link" (spec §7) mails the CURRENT url to the signed-in
 * staff member over the Comms Hub staff channel (POST /roam/send-link —
 * the same rail Roster's notifier uses). An account with no partner
 * tenant, or an environment with the Hub unconfigured, both answer
 * `{sent:false}` — never an error — and this component falls back to
 * copying the link to the clipboard instead, so the action is never a
 * dead end.
 */

export type GateLevel = 'degraded' | 'view-only' | 'blocked';

type SendState = 'idle' | 'sending' | 'sent' | 'copied' | 'error';

@customElement('cos-needs-desktop')
export class CosNeedsDesktop extends LitElement {
	@property({ type: String }) variant: 'block' | 'banner' = 'block';
	@property({ type: String }) level: GateLevel = 'blocked';
	@property({ type: String, attribute: 'surface-label' }) surfaceLabel = 'This screen';
	/** The device-aware handoff phrase — e.g. "a tablet or larger screen"
	 *  or "a desktop". Only meaningful for `variant="block"`. */
	@property({ type: String, attribute: 'device-label' }) deviceLabel = 'a larger screen';
	@property({ type: String }) note = '';
	/** The link to send/copy. Falls back to the current location when
	 *  unset, so a bare `<cos-needs-desktop>` still works standalone. */
	@property({ type: String }) url = '';

	@state() private _sendState: SendState = 'idle';

	static styles = css`
		:host {
			display: block;
		}
		.block {
			display: flex;
			flex-direction: column;
			align-items: center;
			text-align: center;
			gap: var(--cos-space-3);
			max-width: 440px;
			margin: var(--cos-space-8) auto;
			padding: var(--cos-space-6) var(--cos-space-4);
		}
		.block .hero {
			color: var(--cos-text-tertiary);
		}
		.block h2 {
			margin: 0;
			font-size: var(--cos-font-size-lg);
			font-weight: var(--cos-font-weight-medium);
			color: var(--cos-text-primary);
		}
		.block p {
			margin: 0;
			font-size: var(--cos-font-size-sm);
			color: var(--cos-text-secondary);
			line-height: 1.5;
		}
		.banner {
			display: flex;
			align-items: center;
			gap: var(--cos-space-2);
			padding: var(--cos-space-2) var(--cos-space-4);
			background: var(--cos-status-warning-wash);
			color: var(--cos-status-warning-text);
			border-bottom: 1px solid var(--cos-border-soft);
			font-size: var(--cos-font-size-sm);
		}
		.banner cos-icon {
			flex-shrink: 0;
		}
		.banner .text {
			flex: 1;
			min-width: 0;
		}
	`;

	private get _effectiveUrl(): string {
		return this.url || (typeof window !== 'undefined' ? window.location.href : '');
	}

	private async _sendLink(): Promise<void> {
		if (this._sendState === 'sending') return;
		this._sendState = 'sending';
		const url = this._effectiveUrl;
		try {
			const res = await api.sendGateLink(url, this.surfaceLabel);
			if (res?.sent) {
				this._sendState = 'sent';
				void showToast(`We'll email you a link to ${this.surfaceLabel}.`, 'ok');
				return;
			}
			await this._copyFallback(url);
		} catch {
			await this._copyFallback(url);
		}
	}

	private async _copyFallback(url: string): Promise<void> {
		try {
			await navigator.clipboard.writeText(url);
			this._sendState = 'copied';
			void showToast("Link copied: paste it wherever you'll open it on a bigger screen.", 'ok');
		} catch {
			this._sendState = 'error';
			void showToast('Could not copy the link automatically: copy it from the address bar.', 'error');
		}
	}

	private _sendLabel(): string {
		switch (this._sendState) {
			case 'sending':
				return 'Sending…';
			case 'sent':
				return 'Link on its way';
			case 'copied':
				return 'Link copied';
			case 'error':
				return 'Copy manually';
			default:
				return 'Send me this link';
		}
	}

	private _bannerCopy(): string {
		if (this.level === 'view-only') {
			return `${this.surfaceLabel}: view-only on this screen: some actions need ${this.deviceLabel}.`;
		}
		return `${this.surfaceLabel}: not yet certified on this screen size.`;
	}

	render() {
		if (this.variant === 'banner') {
			return html`
				<div class="banner" role="status">
					<cos-icon name="monitor" size="16"></cos-icon>
					<span class="text">${this._bannerCopy()}${this.note ? html` ${this.note}` : nothing}</span>
					<cos-button size="sm" variant="ghost" ?disabled=${this._sendState === 'sending'} @click=${() => this._sendLink()}
						>${this._sendLabel()}</cos-button
					>
				</div>
			`;
		}
		return html`
			<div class="block">
				<cos-icon class="hero" name="monitor" size="40"></cos-icon>
				<h2>${this.surfaceLabel} needs ${this.deviceLabel}</h2>
				<p>${this.note || 'This screen needs more room than a phone gives it.'}</p>
				<cos-button variant="primary" ?disabled=${this._sendState === 'sending'} @click=${() => this._sendLink()}
					>${this._sendLabel()}</cos-button
				>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-needs-desktop': CosNeedsDesktop;
	}
}
